import type { StatusDotColor } from "components/StatusDot";
import type { PaymentStatus } from "../models/PaymentStatus";

export const STATUS_NEW_PAYMENT = "NEW_PAYMENT";

// Initialize authorisation
export const STATUS_BANK_REDIRECT = "BANK_REDIRECT";
export const STATUS_APPROVED_BY_RISK = "APPROVED_BY_RISK";
export const STATUS_ADDITIONAL_AUTHORIZATION_REQUIRED =
  "ADDITIONAL_AUTHORIZATION_REQUIRED";

// Multiple Accounts consent
export const STATUS_ACCOUNT_SELECTION = "ACCOUNT_SELECTION";

// Canceled
export const STATUS_CANCELLED_BY_USER = "CANCELLED_BY_USER";
export const STATUS_FAILED = "FAILED";

// Failed
export const STATUS_REFUSED_BY_RISK = "REFUSED_BY_RISK";
export const STATUS_REFUSED_BY_BANK = "REFUSED_BY_BANK";
export const STATUS_ERROR_AT_BANK = "ERROR_AT_BANK";
export const STATUS_ABANDONED_BY_USER = "ABANDONED_BY_USER";

// Authorized
export const STATUS_AUTHORISED_BY_USER = "AUTHORISED_BY_USER";

// Finished
export const STATUS_COMPLETED = "COMPLETED";
export const STATUS_DELAYED_AT_BANK = "DELAYED_AT_BANK";

// Cash Management
export const STATUS_RECEIVED = "RECEIVED";
export const STATUS_SETTLED = "SETTLED";
export const STATUS_NOT_RECEIVED = "NOT_RECEIVED";

export const STATUS_UNKNOWN = "UNKNOWN";

export const STATUS_GROUP_REQUESTED = "GROUP_REQUESTED";
export const STATUS_GROUP_INITIATED = "GROUP_INITIATED";
export const STATUS_GROUP_PENDING = "GROUP_PENDING";
export const STATUS_GROUP_FAILED = "GROUP_FAILED";
export const STATUS_GROUP_OTHER = "GROUP_OTHER";
export const STATUS_GROUP_CASH_MANAGEMENT = "GROUP_CASH_MANAGEMENT";

export const STATUS_ALL = [
  // from STATUS_GROUP_REQUESTED
  STATUS_NEW_PAYMENT,
  // from STATUS_GROUP_INITIATED
  STATUS_COMPLETED,
  // from STATUS_GROUP_CASH_MANAGEMENT
  STATUS_SETTLED,
  STATUS_RECEIVED,
  STATUS_NOT_RECEIVED,
  // from STATUS_GROUP_PENDING
  STATUS_BANK_REDIRECT,
  STATUS_APPROVED_BY_RISK,
  STATUS_ADDITIONAL_AUTHORIZATION_REQUIRED,
  STATUS_AUTHORISED_BY_USER,
  STATUS_DELAYED_AT_BANK,
  // from STATUS_GROUP_FAILED
  STATUS_REFUSED_BY_RISK,
  STATUS_CANCELLED_BY_USER,
  STATUS_FAILED,
  STATUS_REFUSED_BY_BANK,
  STATUS_ERROR_AT_BANK,
  STATUS_ABANDONED_BY_USER,
  // from STATUS_GROUP_OTHER
  STATUS_UNKNOWN,
] as const;

export const STATUS_GROUPS: Record<string, string | string[]> = {
  [STATUS_GROUP_REQUESTED]: [STATUS_NEW_PAYMENT],
  [STATUS_GROUP_INITIATED]: [STATUS_COMPLETED],
  [STATUS_GROUP_CASH_MANAGEMENT]: [
    STATUS_SETTLED,
    STATUS_RECEIVED,
    STATUS_NOT_RECEIVED,
  ],
  [STATUS_GROUP_PENDING]: [
    STATUS_APPROVED_BY_RISK,
    STATUS_ADDITIONAL_AUTHORIZATION_REQUIRED,
    STATUS_BANK_REDIRECT,
    STATUS_AUTHORISED_BY_USER,
    STATUS_DELAYED_AT_BANK,
  ],
  [STATUS_GROUP_FAILED]: [
    STATUS_REFUSED_BY_RISK,
    STATUS_CANCELLED_BY_USER,
    STATUS_FAILED,
    STATUS_REFUSED_BY_BANK,
    STATUS_ERROR_AT_BANK,
    STATUS_ABANDONED_BY_USER,
  ],
  [STATUS_UNKNOWN]: STATUS_UNKNOWN,
};

type StatusConfig = {
  color: "default" | "warning" | "error" | "success";
};

export const statusConfigs: Readonly<
  {
    [K in PaymentStatus | "DEFAULT"]: StatusConfig;
  }
> = {
  DEFAULT: { color: "default" },

  [STATUS_NEW_PAYMENT]: { color: "default" },

  [STATUS_COMPLETED]: { color: "success" },

  [STATUS_SETTLED]: { color: "success" },
  [STATUS_NOT_RECEIVED]: { color: "error" },
  [STATUS_RECEIVED]: { color: "success" },

  [STATUS_APPROVED_BY_RISK]: { color: "success" },
  [STATUS_ADDITIONAL_AUTHORIZATION_REQUIRED]: { color: "error" },
  [STATUS_BANK_REDIRECT]: { color: "warning" },
  [STATUS_AUTHORISED_BY_USER]: { color: "default" },
  [STATUS_DELAYED_AT_BANK]: { color: "warning" },

  [STATUS_REFUSED_BY_RISK]: { color: "error" },
  [STATUS_CANCELLED_BY_USER]: { color: "error" },
  [STATUS_FAILED]: { color: "error" },
  [STATUS_REFUSED_BY_BANK]: { color: "error" },
  [STATUS_ERROR_AT_BANK]: { color: "error" },
  [STATUS_ABANDONED_BY_USER]: { color: "error" },

  [STATUS_UNKNOWN]: { color: "error" },
} as const;

export const MAP_STATUS_COLOR_TO_DOT_COLOR: Record<
  StatusConfig["color"],
  StatusDotColor
> = {
  success: "success",
  error: "failure",
  warning: "warning",
  default: "low",
};
